import { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useScrimDismiss } from '../hooks/useScrimDismiss';
import { useEscapeClose } from '../hooks/useEscapeClose';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
    View,
} from '@ea-lab/reactive-json';

export const Modal = ({ props, currentData, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const mainAttributesHolderRef = useRef(null);

    const attributes = useEvaluatedAttributes(props.attributes);
    const className = props.className || '';

    // Determine if modal should be shown
    const showBoolPath = props.showBoolPath;
    let isShown = false;

    if (typeof showBoolPath === 'boolean') {
        isShown = showBoolPath;
    } else if (typeof showBoolPath === 'string') {
        // Evaluate the path to get the boolean value
        const evaluated = evaluateTemplateValue({
            valueToEvaluate: showBoolPath,
            globalDataContext,
            templateContext,
        });
        isShown = Boolean(evaluated);
    } else {
        // Default to false if not specified
        isShown = false;
    }

    const handleClose = () => {
        if (typeof showBoolPath === 'string') {
            // Update the data path to false
            try {
                // Evaluate the path using template system
                const evaluated = evaluateTemplateValue({
                    valueToEvaluate: showBoolPath,
                    globalDataContext,
                    templateContext,
                });
                
                // If it's a path string like "~~.showModal", extract the path
                if (typeof showBoolPath === 'string') {
                    let dataPath = showBoolPath.replace(/^~~\./, '').replace(/^~\./, '');
                    globalDataContext.updateData(false, dataPath);
                }
            } catch (e) {
                console.warn('Could not update modal visibility:', e);
            }
        }
    };

    // `lockPath` : chemin d'un booléen du store. Vrai → la modale ne se ferme
    // NI par Échap, NI par un clic sur le fond, NI par la croix (masquée) ; ses
    // propres boutons restent seuls maîtres. Pour une écriture longue : fermée,
    // la modale n'est plus rendue, et le bouton qui porte l'action
    // `on: response` disparaîtrait avec elle — la fin de requête ne serait plus
    // entendue (indicateur « en cours » figé, échec jamais affiché).
    const isLocked = typeof props.lockPath === 'string'
        ? Boolean(evaluateTemplateValue({
            valueToEvaluate: props.lockPath,
            globalDataContext,
            templateContext,
        }))
        : Boolean(props.lockPath);

    // Une fenêtre déclarée modale doit RECEVOIR le focus à l'ouverture et le
    // rendre à la fermeture (RGAA 7.3) — sans quoi le clavier reste derrière
    // elle. On ne le déplace pas s'il est déjà dedans (champ en autofocus).
    const dialogRef = useRef(null);
    useEffect(() => {
        if (!isShown) return undefined;
        const previous = document.activeElement;
        const node = dialogRef.current;
        if (node && !node.contains(previous)) node.focus({ preventScroll: true });
        return () => {
            if (previous instanceof HTMLElement && document.contains(previous)) previous.focus({ preventScroll: true });
        };
    }, [isShown]);

    const scrim = useScrimDismiss(handleClose, !isLocked);
    useEscapeClose(handleClose, isShown && !isLocked);

    if (!isShown) {
        return null;
    }

    const closeButton = props.closeButton ?? false;

    const dialog = (
        <ActionDependant {...props} attributesHolderRef={mainAttributesHolderRef}>
            <div
                className={`modal-overlay fixed inset-0 bg-slate-900/40 dark:bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${className}`}
                {...scrim}
                {...attributes}
            >
                <div
                    className={`modal-content bg-white dark:bg-slate-800 rounded-lg shadow-2xl ${props.maxWidthClass || 'max-w-2xl'} w-full max-h-[90vh] overflow-y-auto`}
                    // Une fenêtre modale s'annonce comme telle (RGAA 7.1) : sans rôle,
                    // un lecteur d'écran ne sait ni qu'elle s'est ouverte, ni où elle
                    // s'arrête. `ariaLabel` (facultatif) lui donne son nom.
                    role="dialog"
                    aria-modal="true"
                    aria-label={typeof props.ariaLabel === 'string' && props.ariaLabel ? props.ariaLabel : 'Fenêtre de dialogue'}
                    tabIndex={-1}
                    ref={dialogRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    {(props.headerTitle || closeButton) && (
                        <div className="modal-header sticky top-0 z-10 bg-white dark:bg-slate-800 rounded-t-lg border-b border-slate-200 dark:border-slate-700 p-4 flex justify-between items-center gap-3 text-slate-800 dark:text-slate-100">
                            <div className="flex-1">
                                {props.headerTitle && (
                                    <View
                                        currentData={currentData?.headerTitle}
                                        datafield="headerTitle"
                                        path={path + '.headerTitle'}
                                        props={props.headerTitle}
                                    />
                                )}
                            </div>
                            {closeButton && !isLocked && (
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="modal-close dark:text-slate-400 dark:hover:text-slate-300"
                                    aria-label="Fermer"
                                >
                                    <X aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    )}
                    {props.body && (
                        <div className="modal-body p-4">
                            <View
                                currentData={currentData?.body}
                                datafield="body"
                                path={path + '.body'}
                                props={props.body}
                            />
                        </div>
                    )}
                    {props.footer && (
                        <div className="modal-footer sticky bottom-0 z-10 bg-white dark:bg-slate-800 rounded-b-lg border-t border-slate-200 dark:border-slate-700 p-4 flex justify-end gap-2">
                            <View
                                currentData={currentData?.footer}
                                datafield="footer"
                                path={path + '.footer'}
                                props={props.footer}
                            />
                        </div>
                    )}
                </div>
            </div>
        </ActionDependant>
    );

    // `portal: true` rend la modale dans <body>. Sans portail, le voile
    // `position: fixed` se positionne par rapport au premier ancêtre TRANSFORMÉ
    // ou FILTRÉ, pas à l'écran — et un widget en pose : une grille qui déplace
    // ses cellules par `transform`, ou le builder qui grise en édition les
    // régions dynamiques par `filter`. La modale s'y ouvrirait piégée. Opt-in :
    // les modales existantes, hors widget, gardent leur rendu à l'identique. Le
    // contexte React traverse le portail, donc le contenu lit toujours le store
    // du bloc.
    if (props.portal && typeof document !== 'undefined') {
        return createPortal(dialog, document.body);
    }
    return dialog;
};
